import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Hero from '@/components/Hero';
import RideForm from '@/components/RideForm';
import RideConfirmation from '@/components/RideConfirmation';
import AboutSection from '@/components/AboutSection';
import { RideFormData } from '@/types/ride';
import { useAuth } from '@/hooks/auth/useAuth';
import Header from '@/components/Header';
import Container from '@/components/Container';
import Footer from '@/components/Footer';
import Button from '@/components/Button';
import Link from '@/components/Link';
import { MobileBreadcrumbs } from '@/components/ui/mobile-breadcrumbs';
import { OnboardingTooltip } from '@/components/ui/onboarding-tooltip';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Car, DollarSign, Handshake, Star } from 'lucide-react';
import { MobileRideForm } from '@/components/mobile/MobileRideForm';
import { useIsMobile } from '@/hooks/use-mobile';
import { MobileContainer, MobileFixedBottom } from '@/components/ui/mobile-container';

const Index = () => {
  const [showRideConfirmation, setShowRideConfirmation] = useState(false);
  const [rideDetails, setRideDetails] = useState<RideFormData | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const handleRideSubmit = (data: RideFormData) => {
    if (!user) {
      navigate('/auth', { 
        state: { 
          from: '/',
          message: 'Please sign in to book a ride and start negotiating with drivers'
        }
      });
      return;
    }
    
    setRideDetails(data);
    setShowRideConfirmation(true);
  };
  
  const handleRideConfirm = () => {
    setShowRideConfirmation(false);
    navigate('/rides');
  };
  
  const handleRideCancel = () => {
    setShowRideConfirmation(false);
  };
  
  if (isMobile) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-b from-background to-primary/5">
        <Header />
        <main id="main-content" className="flex-1">
          <Hero />

          {/* Mobile Edge-to-Edge Booking Section */}
          <section id="booking" className="min-h-screen">
            {showRideConfirmation && rideDetails ? (
              <MobileContainer className="p-4 animate-fade-in">
                <RideConfirmation 
                  rideDetails={rideDetails} 
                  onConfirm={handleRideConfirm}
                  onCancel={handleRideCancel}
                />
              </MobileContainer>
            ) : (
              <div className="space-y-0">
                {/* How it Works - Edge to Edge */}
                <div className="bg-gradient-to-r from-primary/5 to-secondary/5 p-4 border-b border-muted/20">
                  <Card className="bg-white/50 backdrop-blur-sm border-0 shadow-sm">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Handshake className="h-5 w-5 text-primary" />
                          How ParleyRide Works
                        </CardTitle>
                        <OnboardingTooltip
                          title="Why Negotiate?"
                          content="Unlike other ride apps, ParleyRide lets you negotiate prices directly with drivers. This means you can often get better rates, especially for longer trips or during off-peak hours!"
                          showOnFirstVisit={true}
                          storageKey="how-it-works-seen"
                        >
                          <span></span>
                        </OnboardingTooltip>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="space-y-2">
                          <div className="bg-primary/20 rounded-full p-3 mx-auto w-fit">
                            <Car className="h-4 w-4 text-primary" />
                          </div>
                          <p className="text-xs font-medium">1. Request</p>
                        </div>
                        <div className="space-y-2">
                          <div className="bg-secondary/20 rounded-full p-3 mx-auto w-fit">
                            <DollarSign className="h-4 w-4 text-secondary" />
                          </div>
                          <p className="text-xs font-medium">2. Negotiate</p>
                        </div>
                        <div className="space-y-2">
                          <div className="bg-green-500/20 rounded-full p-3 mx-auto w-fit">
                            <Star className="h-4 w-4 text-green-500" />
                          </div>
                          <p className="text-xs font-medium">3. Ride</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Mobile Ride Form - Edge to Edge */}
                <MobileRideForm onSubmit={handleRideSubmit} />
              </div>
            )}
          </section>
        </main>
      </div>
    );
  }

  // Desktop version with existing layout
  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-background to-primary/5">
      <Header />
      <main id="main-content" className="flex-1">
        <Hero />
        <Container>
          {/* Enhanced Booking Section */}
          <section id="booking" className="py-12 px-4">
            <div className="container mx-auto max-w-4xl">
              {showRideConfirmation && rideDetails ? (
                <div className="animate-fade-in">
                  <RideConfirmation 
                    rideDetails={rideDetails} 
                    onConfirm={handleRideConfirm}
                    onCancel={handleRideCancel}
                  />
                </div>
              ) : (
                <RideForm onSubmit={handleRideSubmit} />
              )}
            </div>
          </section>
          
          {/* Add a call-to-action for driver signup */}
          <section className="py-12 md:py-16 border-t border-muted">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold tracking-tight">Drive with ParleyRide</h2>
                <p className="text-muted-foreground text-lg">
                  Earn money on your own schedule. Sign up to drive with ParleyRide and start earning today.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild size="lg" className="bg-gradient-to-r from-ride-primary to-ride-secondary hover:opacity-90">
                    <Link to="/driver-signup">Become a Driver</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link to="/how-it-works">Learn More</Link>
                  </Button>
                </div>
              </div>
              <div className="bg-muted rounded-lg p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-background rounded-md p-4 text-center">
                    <h3 className="text-2xl font-bold text-ride-primary">Flexible</h3>
                    <p className="text-sm text-muted-foreground">Work on your schedule</p>
                  </div>
                  <div className="bg-background rounded-md p-4 text-center">
                    <h3 className="text-2xl font-bold text-ride-primary">Better Pay</h3>
                    <p className="text-sm text-muted-foreground">Keep more of what you earn</p>
                  </div>
                  <div className="bg-background rounded-md p-4 text-center">
                    <h3 className="text-2xl font-bold text-ride-primary">Price Control</h3>
                    <p className="text-sm text-muted-foreground">Negotiate your rates</p>
                  </div>
                  <div className="bg-background rounded-md p-4 text-center">
                    <h3 className="text-2xl font-bold text-ride-primary">Freedom</h3>
                    <p className="text-sm text-muted-foreground">Be your own boss</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
          
          {/* About Section */}
          <AboutSection />
        </Container>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
